import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    CircularProgress,
    Divider,
    Link,
    Checkbox,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    ListItem,
    ListItemText,
    List,
    Tabs,
    FormControlLabel,
    Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterIcon from '@mui/icons-material/FilterList';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { format, startOfMonth, endOfMonth, parseISO, isValid, startOfWeek, endOfWeek, subDays, subMonths } from 'date-fns';
import { teleConsultationModel, shopModel } from '../Axios/AxiosConfig';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import TimelineIcon from '@mui/icons-material/Timeline'; // For track button
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import axios from 'axios';
import { toast } from 'react-toastify';

interface Subscription {
    subscriptionId: string;
    userId: string;
    planName: string;
    price: number;
    discount: number;
    finalPrice: number;
    purchaseDate: string;
    teleconsultationlimit: number;
    products_discount: number;
    duration: number;
    expirationDate: string;
    status: 'Active' | 'Expired' | 'Inactive';
    payment_status?: string;
    paymentDetails?: {
        transactionId?: string;
        paymentMethod?: string;
    };
    profile?: any;
    corporateUser?: any;
    Invoice?: any;
    Remarks?: any;
}

const SubscriptionsPage: React.FC = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [page, setPage] = useState<number>(0);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState<string[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState(0);
    const [generatingInvoiceId, setGeneratingInvoiceId] = useState<string | null>(null);
    const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
    const [openDateRangeDialog, setOpenDateRangeDialog] = useState<boolean>(false);

    const [dateRange, setDateRange] = useState<{ startDate: any; endDate: any }>({
        startDate: '',
        endDate: ''
    });

    const [analytics, setAnalytics] = useState({
        total: 0,
        active: 0,
        expired: 0,
        individual: { total: 0, active: 0, expired: 0, revenue: 0 },
        corporate: { total: 0, active: 0, expired: 0, revenue: 0 }
    });
    const [trackingData, setTrackingData] = useState<any[]>([]);
    const [trackingLoading, setTrackingLoading] = useState<boolean>(false);
    const [trackingDialogOpen, setTrackingDialogOpen] = useState<boolean>(false);
    const [remarks, setRemarks] = useState<any[]>([]);
    const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
    const [editingRemark, setEditingRemark] = useState<any | null>(null);
    const [remarkText, setRemarkText] = useState("");
    const [remark_updated_by, setremark_updated_by] = useState<string | null>(null);
    const [remarkCompleted, setRemarkCompleted] = useState(false);
    const [savingRemark, setSavingRemark] = useState(false);

    const calculateAnalytics = (subscriptionsToAnalyze: Subscription[]) => {
        const stats = {
            total: subscriptionsToAnalyze.length,
            active: 0,
            expired: 0,
            individual: { total: 0, active: 0, expired: 0, revenue: 0 },
            corporate: { total: 0, active: 0, expired: 0, revenue: 0 }
        };

        subscriptionsToAnalyze.forEach(sub => {
            const isCorporate = !!sub.corporateUser;
            const isActive = sub.status === 'Active';
            const isExpired = sub.status === 'Expired';
            const finalPrice = parseFloat(sub?.finalPrice?.toString()) || 0;
            const price = parseFloat(sub?.price?.toString()) || 0;

            if (isCorporate) {
                stats.corporate.total++;
                if (isActive) { stats.corporate.active++; stats.active++; }
                if (isExpired) { stats.corporate.expired++; stats.expired++; }
                if (sub.status !== 'Inactive') stats.corporate.revenue += price;
            } else {
                stats.individual.total++;
                if (isActive) { stats.individual.active++; stats.active++; }
                if (isExpired) { stats.individual.expired++; stats.expired++; }
                if (sub.status !== 'Inactive') stats.individual.revenue += finalPrice;
            }
        });
        return stats;
    };

    const dateRangePresets = [
        { label: 'Today', getRange: () => ({ startDate: format(new Date(), 'yyyy-MM-dd'), endDate: format(new Date(), 'yyyy-MM-dd') }) },
        { label: 'Yesterday', getRange: () => ({ startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'), endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd') }) },
        { label: 'This Week', getRange: () => ({ startDate: format(startOfWeek(new Date()), 'yyyy-MM-dd'), endDate: format(new Date(), 'yyyy-MM-dd') }) },
        { label: 'Last Week', getRange: () => ({ startDate: format(startOfWeek(subDays(new Date(), 7)), 'yyyy-MM-dd'), endDate: format(endOfWeek(subDays(new Date(), 7)), 'yyyy-MM-dd') }) },
        { label: 'This Month', getRange: () => ({ startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'), endDate: format(new Date(), 'yyyy-MM-dd') }) },
        { label: 'Last Month', getRange: () => ({ startDate: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'), endDate: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd') }) },
        { label: 'This Year', getRange: () => ({ startDate: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'), endDate: format(new Date(), 'yyyy-MM-dd') }) },
        { label: 'Get All', getRange: () => ({ startDate: undefined, endDate: undefined }) }
    ];

    const fetchSubscriptions = async (startDate?: any, endDate?: any) => {
        try {
            setLoading(true);
            setError(null);
            const response = await teleConsultationModel.get('/getSubscriptionList', { params: { startDate, endDate } });
            const data = response.data;
            if (data.subscriptionList) {
                setSubscriptions(data.subscriptionList);
                setAnalytics(calculateAnalytics(data.subscriptionList));
            } else {
                setSubscriptions([]);
                setAnalytics({
                    total: 0, active: 0, expired: 0,
                    individual: { total: 0, active: 0, expired: 0, revenue: 0 },
                    corporate: { total: 0, active: 0, expired: 0, revenue: 0 }
                });
            }
        } catch (err: any) {
            setError('Failed to fetch subscriptions');
            console.error('Error fetching subscriptions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRemarkClick = () => {
        setEditingRemark(null);
        setRemarkText("");
        setRemarkCompleted(false);
        setRemarkDialogOpen(true);
    };

    const handleEditRemarkClick = (remark: any) => {
        setEditingRemark(remark);
        setRemarkText(remark.remark_name || "");
        setRemarkCompleted(!!remark.remark_completed);
        setremark_updated_by(remark.remark_updated_by || "");
        setRemarkDialogOpen(true);
    };

    const handleCloseRemarkDialog = () => {
        setRemarkDialogOpen(false);
        setEditingRemark(null);
    };


    const handleSaveRemark = async () => {
        if (!selectedSubscription?.userId) {
            console.log("UID missing on selectedSubscription");
            return;
        }

        if (!remarkText.trim()) {
            console.log("Remark text is empty");
            return;
        }

        setSavingRemark(true);

        try {
            const body = {
                UID: selectedSubscription?.userId,
                remark_id: editingRemark?.remark_id || Date.now()?.toString(),
                remark_name: remarkText.trim(),
                remark_completed: remarkCompleted,
                remark_updated_by: remark_updated_by, // adjust as needed
            };

            const res = await shopModel.post("/api/v1/subscription-remarks", body);

            const resData = res.data;

            const { data } = resData

            setRemarks(data?.Remarks || []);

            setSelectedSubscription(data || []);

            setRemarkDialogOpen(false);

            setEditingRemark(null);
        } catch (err: any) {
            console.error("Failed to save remark:", err);
        } finally {
            setSavingRemark(false);
        }
    };

    const getCorpStatusColor = (status: string) => {
        switch (status) {
            case 'Corporate User': return 'linear-gradient(45deg, #e8f5e9 30%, #f1f8e9 90%)';
            case 'Individual User': return 'linear-gradient(45deg, #e3f2fd 30%, #f0f7ff 90%)';
            default: return 'primary';
        }
    };



    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedSubscription(null);
        setTrackingData([]);
    };

    const fetchUserProfile = async (userId: string, subscription: Subscription) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_AUTH_URL}searchUser`,
                { params: { search: userId } }
            );

            if (response.status === 200 && Array.isArray(response.data)) {
                const user = response.data[0];
                if (!user) return;
                const attributes = user.attributes || {};

                if (!subscription.profile) subscription.profile = {};

                const name = attributes.name || "";
                const parts = name.includes(" ") ? name.split(" ") : [name];

                subscription.profile.firstname = parts[0] || "";
                subscription.profile.lastname = parts.slice(1).join(" ") || "";
                subscription.profile.email = attributes.email || "";
                subscription.profile.phone = attributes.phone_number || "";
                subscription.profile.role = attributes.role || "";

                setSelectedSubscription({ ...subscription, profile: { ...subscription.profile } });
                setDetailsOpen(true);
            }
        } catch (error) {
            console.error("Error fetching user profile:", error);
        }
    };


    const hasValidProfile = (profile?: any) =>
        profile &&
        profile.firstname &&
        profile.lastname &&
        profile.firstname !== "undefined" &&
        profile.lastname !== "undefined";

    const handleOpenDetails = (subscription: Subscription) => {
        if (!hasValidProfile(subscription.profile)) {
            fetchUserProfile(subscription.userId, subscription);
        }

        setSelectedSubscription(subscription);
        setRemarks(subscription.Remarks || []);
        setDetailsOpen(true);
    };

    const getCurrentMonthRange = () => {
        const now = new Date();
        const currentStartDate = format(startOfMonth(now), 'yyyy-MM-dd');
        const currentEndDate = format(endOfMonth(now), 'yyyy-MM-dd');
        return { startDate: currentStartDate, endDate: currentEndDate };
    };

    useEffect(() => {
        const { startDate, endDate } = getCurrentMonthRange();
        fetchSubscriptions(startDate, endDate);
    }, []);

    useEffect(() => {
        if (subscriptions.length > 0) {
            setAnalytics(calculateAnalytics(
                subscriptions.filter(sub => {
                    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
                    const matchesUserType =
                        userTypeFilter === 'all' ||
                        (userTypeFilter === 'corporate' && sub.corporateUser) ||
                        (userTypeFilter === 'individual' && !sub.corporateUser);
                    return matchesStatus && matchesUserType;
                })
            ));
        }
    }, [subscriptions, statusFilter, userTypeFilter]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleStatusFilterChange = (e: any) => {
        setStatusFilter(e.target.value);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDateRangeDialog = () => {
        setOpenDateRangeDialog(true);
    };

    const handleCloseDateRangeDialog = () => {
        setOpenDateRangeDialog(false);
    };

    const handleDateRangeApply = () => {
        fetchSubscriptions(dateRange.startDate, dateRange.endDate);
        handleCloseDateRangeDialog();
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setUserTypeFilter('all');
        setSelectedSubscriptions([]);
        const { startDate, endDate } = getCurrentMonthRange();
        fetchSubscriptions(startDate, endDate);
    };

    const fetchTrackingData = async (userId: string) => {
        try {
            setTrackingLoading(true);
            setTrackingData([]);
            const appointmentsPromise = teleConsultationModel.get(`/getAllAppointments/${userId}`, {
            });


            const ordersPromise = shopModel.post('/getOrders', { UID: userId });

            const [appointmentsRes, ordersRes] = await Promise.allSettled([appointmentsPromise, ordersPromise]);

            let combinedData: any[] = [];

            if (appointmentsRes.status === 'fulfilled' && appointmentsRes.value.data) {
                const appts = appointmentsRes.value.data.filter((apt: any) => apt.patient_id === userId || apt.UID === userId).filter((apt: any) => apt.appointment_status !== 'initiated');

                const mappedAppts = appts.map((apt: any) => ({
                    type: 'Consultation',
                    date: apt.appointment_date || apt.timestamp,
                    status: apt.appointment_status || apt.consultation_status,
                    details: apt,
                    id: apt.appointmentID || apt.Appointment_id
                }));
                combinedData = [...combinedData, ...mappedAppts];
            }

            if (ordersRes.status === 'fulfilled' && ordersRes.value.data) {
                const orders = Array.isArray(ordersRes.value.data) ? ordersRes.value.data : [];

                const mappedOrders = orders.map((order: any) => ({
                    type: 'Order',
                    date: order.Data?.dateandtime || order.timestamp, // check structure
                    status: order.Data?.OrderStatus,
                    details: order,
                    id: order.id
                }));
                combinedData = [...combinedData, ...mappedOrders];
            }

            combinedData.sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;

                if (isNaN(dateA)) return -1;
                if (isNaN(dateB)) return 1;

                return dateA - dateB;
            });

            setTrackingData(combinedData);

        } catch (error) {
            console.error("Error fetching tracking data", error);
            toast.error("Failed to load history");
        } finally {
            setTrackingLoading(false);
        }
    };

    const handleOpenTracking = (subscription: Subscription) => {
        if (subscription.userId) {
            setSelectedSubscription(subscription); // Set selected sub to ensure context
            setDetailsOpen(true);
            setActiveTab(1);
            fetchTrackingData(subscription.userId);
        } else {
            toast.warning("No User ID found for this subscription");
        }
    };

    const handleCloseTracking = () => {
        // Deprecated, use handleCloseDetails
        handleCloseDetails();
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 1 && selectedSubscription?.userId && trackingData.length === 0) {
            fetchTrackingData(selectedSubscription.userId);
        }
    };

    const getSubscriptionInvoiceUrl = (subscription?: Subscription | null): string | undefined => {
        if (!subscription) return undefined;

        const invoice: any = subscription.Invoice;
        if (!invoice) return undefined;

        if (typeof invoice === 'string') {
            return invoice;
        }

        if (typeof invoice === 'object') {
            return invoice.Invoice || invoice.invoiceUrl || invoice.url;
        }

        return undefined;
    };

    const handleGenerateSubscriptionInvoice = async (subscription: Subscription) => {
        if (!subscription?.subscriptionId) return;

        try {
            setGeneratingInvoiceId(subscription.subscriptionId);

            const response = await shopModel.get(`/generate-subscription-invoice/${subscription.subscriptionId}`);
            const { message, invoiceUrl } = response?.data || {};

            toast.success(message || 'Subscription invoice generated successfully');

            if (invoiceUrl) {
                if (selectedSubscription && selectedSubscription.subscriptionId === subscription.subscriptionId) {
                    setSelectedSubscription(prev => prev ? {
                        ...prev,
                        Invoice: invoiceUrl
                    } : prev);
                }

                setSubscriptions(prev =>
                    prev.map(sub =>
                        sub.subscriptionId === subscription.subscriptionId
                            ? { ...sub, Invoice: invoiceUrl }
                            : sub
                    )
                );
            }

            await fetchSubscriptions(dateRange.startDate, dateRange.endDate);
        } catch (error: any) {
            console.error('Error generating subscription invoice:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to generate invoice');
        } finally {
            setGeneratingInvoiceId(null);
        }
    };

    const handleViewSubscriptionInvoice = (subscription: any) => {
        const invoiceUrl = getSubscriptionInvoiceUrl(subscription);

        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank');
        }
    };

    const filteredSubscriptions = subscriptions.filter(subscription => {
        const matchesSearch =
            subscription.planName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            subscription.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (subscription.paymentDetails?.transactionId?.toLowerCase().includes(searchTerm.toLowerCase()) || '');

        const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter;

        const matchesUserType =
            userTypeFilter === 'all' ||
            (userTypeFilter === 'corporate' && subscription.corporateUser) ||
            (userTypeFilter === 'individual' && !subscription.corporateUser);

        return matchesSearch && matchesStatus && matchesUserType;
    });

    const getSubscriptionDisplayName = (subscription: Subscription) => {
        const profile = subscription.profile || {};
        const firstname = profile.firstname || '';
        const lastname = profile.lastname || '';
        const fullName = `${firstname} ${lastname}`.trim();

        if (fullName) {
            return fullName;
        }

        const uid = subscription.userId;
        const contact = profile.email || profile.phone;

        if (uid && contact) {
            return `${uid} (${contact})`;
        }

        return uid || contact || 'N/A';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Expired': return 'error';
            case 'Inactive': return 'warning';
            default: return 'default';
        }
    };

    const handleSelectSubscription = (subscriptionId: string, isSelected: boolean) => {
        if (isSelected) {
            setSelectedSubscriptions(prev => [...prev, subscriptionId]);
        } else {
            setSelectedSubscriptions(prev => prev.filter(id => id !== subscriptionId));
        }
    };

    const handleSelectAll = (isSelected: boolean) => {
        if (isSelected) {
            setSelectedSubscriptions(filteredSubscriptions.map(sub => sub.subscriptionId));
        } else {
            setSelectedSubscriptions([]);
        }
    };

    const handleDeleteSelected = () => {
        console.log(selectedSubscriptions);
        if (selectedSubscriptions.length === 0) return;
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const deletePromises = selectedSubscriptions.map(subscriptionId =>
                teleConsultationModel.post(`/deleteSubscription/`, { subscriptionId })
            );
            await Promise.all(deletePromises);
            toast.success('Subscriptions deleted successfully');
        } catch (error) {
            toast.error('Error deleting subscriptions');
        } finally {
            setDeleteDialogOpen(false);
            setLoading(false);
            await fetchSubscriptions(dateRange.startDate, dateRange.endDate);
            setSelectedSubscriptions([]);
        }
    };

    console.log(selectedSubscription)

    return (
        <Box sx={{ p: 3, pt: 1.5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontFamily: "Libre Baskerville", color: "#3B362A" }}>
                    Subscriptions
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Manage and view all subscription details
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>

                {/* Total Subscriptions */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            flex: 1, // 
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            background: "linear-gradient(45deg, #f4ecd6 30%, #f8f8f4 90%)",
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                    >
                        <CardContent
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between", // 
                                height: "100%", // 
                            }}
                        >
                            <Box>
                                <Typography color="textSecondary" gutterBottom>
                                    Total Subscriptions
                                </Typography>
                                <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                                    {analytics.total}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: 2,
                                }}
                            >
                                <Chip
                                    label={`Active: ${analytics.active}`}
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                />
                                <Chip
                                    label={`Expired: ${analytics.expired}`}
                                    color="error"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                />
                            </Box>
                        </CardContent>

                    </Card>
                </Grid>

                {/* Individual Users */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            background: "linear-gradient(45deg, #e3f2fd 30%, #f0f7ff 90%)",
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                    >
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography color="textSecondary" gutterBottom>
                                    Individual Users
                                </Typography>
                                <PersonIcon color="primary" />
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                                {analytics.individual.total}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Active: <strong>{analytics.individual.active}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Expired: <strong>{analytics.individual.expired}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                            sx={{ mt: 1, fontWeight: 500 }}
                                        >
                                            Revenue: ₹{analytics.individual.revenue.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Corporate Users */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            background: "linear-gradient(45deg, #e8f5e9 30%, #f1f8e9 90%)",
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                    >
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography color="textSecondary" gutterBottom>
                                    Corporate Users
                                </Typography>
                                <BusinessIcon color="success" />
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
                                {analytics.corporate.total}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Active: <strong>{analytics.corporate.active}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Expired: <strong>{analytics.corporate.expired}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            color="success.main"
                                            sx={{ mt: 1, fontWeight: 500 }}
                                        >
                                            Revenue: ₹{analytics.corporate.revenue.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>


            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="stretch">
                        {/* Search */}
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search subscriptions..."
                                value={searchTerm}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                                }}
                                size="small"
                            />
                        </Grid>


                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Expired">Expired</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>User Type</InputLabel>
                                <Select
                                    value={userTypeFilter}
                                    onChange={(e: any) => setUserTypeFilter(e.target.value)}
                                    label="User Type"
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    <MenuItem value="individual">Individual</MenuItem>
                                    <MenuItem value="corporate">Corporate</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleOpenDateRangeDialog}
                                startIcon={<EventNoteIcon />}
                                size="small"
                            >
                                {dateRange.startDate && dateRange.endDate
                                    ? `${format(new Date(dateRange.startDate), "MMM dd, yyyy")} - ${format(
                                        new Date(dateRange.endDate),
                                        "MMM dd, yyyy"
                                    )}`
                                    : "Date Range"}
                            </Button>
                        </Grid>


                        <Grid item xs="auto" sx={{ display: "flex", gap: 1, ml: "auto" }}>
                            <Button
                                variant="outlined"
                                onClick={handleResetFilters}
                                startIcon={<RefreshIcon />}
                                size="small"
                            >
                                Reset
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => fetchSubscriptions()}
                                startIcon={<RefreshIcon />}
                                size="small"
                            >
                                Refresh
                            </Button>
                        </Grid>

                        <Grid item xs="auto" sx={{ display: "flex", gap: 1, ml: "auto" }}>
                            <Button
                                variant="contained"
                                onClick={handleDeleteSelected}
                                disabled={selectedSubscriptions.length === 0}
                                startIcon={<DeleteIcon />}
                                size="small"
                                sx={{
                                    bgcolor: "#dc3545",
                                    "&:hover": { bgcolor: "#c82333" },
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    '&:disabled': {
                                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                                    }
                                }}
                            >
                                Delete ({selectedSubscriptions.length})
                            </Button>
                        </Grid>

                    </Grid>
                </CardContent>
            </Card>

            <Card style={{ boxSizing: 'border-box' }}>
                <CardContent>
                    <TableContainer sx={{
                        overflowX: "auto",
                        "&::-webkit-scrollbar": { height: "10px" },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "10px" }
                    }}>
                        <Box sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                            backgroundColor: '#efefe7',
                            boxSizing: 'border-box',
                            minWidth: '100%',

                        }}>
                            <Typography variant="h6" sx={{ fontFamily: "Libre Baskerville", fontWeight: "700", color: "#3B362A" }}>
                                Subscription List
                            </Typography>
                        </Box>
                        <Table>
                            <TableHead style={{ backgroundColor: '#f4ecd6' }}>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedSubscriptions.length > 0 && selectedSubscriptions.length < filteredSubscriptions.length}
                                            checked={filteredSubscriptions.length > 0 && selectedSubscriptions.length === filteredSubscriptions.length}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Plan Name</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Price</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>User Type</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Remarks</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ backgroundColor: '#f8f8f4' }}>
                                {loading ? (

                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ color: 'error.main' }}>
                                            {error}
                                        </TableCell>
                                    </TableRow>
                                ) : filteredSubscriptions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No subscriptions found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredSubscriptions
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((subscription) => (
                                            <TableRow key={subscription.subscriptionId}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedSubscriptions?.includes(subscription.subscriptionId)}
                                                        onChange={(e) => handleSelectSubscription(subscription.subscriptionId, e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>{getSubscriptionDisplayName(subscription)}</TableCell>
                                                <TableCell>{subscription?.planName}</TableCell>

                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <CurrencyRupeeIcon fontSize="small" />
                                                        {subscription.finalPrice}
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                >
                                                    <Chip
                                                        label={subscription?.corporateUser ? "Corporate User" : "Individual User"}
                                                        size="small"
                                                        sx={{
                                                            background: subscription?.corporateUser ? getCorpStatusColor("Corporate User") : getCorpStatusColor("Individual User"), // light bg
                                                            color: "#000",
                                                            fontWeight: 400,
                                                            justifyContent: "center",
                                                            p: 2,
                                                        }}
                                                    />

                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subscription.status}
                                                        color={getStatusColor(subscription.status) as any}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ p: 2, width: "250px", maxWidth: "250px" }}>
                                                    {subscription?.Remarks?.length > 0 ? (
                                                        <Chip
                                                            label={subscription?.Remarks[subscription?.Remarks?.length - 1]?.remark_name}
                                                            size="small"
                                                            sx={{
                                                                background: "#f5f5f5",
                                                                color: "#000",
                                                                fontWeight: 400,
                                                                justifyContent: "center",
                                                                p: 2,
                                                                height: 'auto',
                                                                '& .MuiChip-label': {
                                                                    display: 'block',
                                                                    whiteSpace: 'normal',
                                                                    padding: '8px',
                                                                    wordBreak: 'break-word',
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="-"
                                                            size="small"
                                                            sx={{
                                                                background: "#f5f5f5",
                                                                color: "#000",
                                                                fontWeight: 400,
                                                                justifyContent: "center",
                                                                p: 1,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => {
                                                            handleOpenDetails(subscription)
                                                        }}
                                                    >
                                                        View
                                                    </Button>

                                                </TableCell>
                                            </TableRow>
                                        ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredSubscriptions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </CardContent>
            </Card>


            <Dialog
                open={openDateRangeDialog}
                onClose={handleCloseDateRangeDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        p: 2,
                    },
                }}
            >
                <DialogTitle>Select Date Range</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                value={dateRange.startDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="End Date"
                                value={dateRange.endDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: dateRange.startDate
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Quick Select:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {dateRangePresets.map((preset) => (
                                    <Button
                                        key={preset.label}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const range = preset.getRange();
                                            setDateRange({
                                                startDate: range.startDate,
                                                endDate: range.endDate
                                            });
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.7rem',
                                            py: 0.25,
                                            px: 1,
                                            minWidth: 'auto',
                                            borderRadius: 1,
                                            borderColor: '#e0e0e0',
                                            '&:hover': {
                                                borderColor: '#3B362A',
                                                backgroundColor: 'rgba(59, 54, 42, 0.04)'
                                            },
                                            color: '#3B362A',
                                            fontWeight: 400
                                        }}
                                    >
                                        {preset.label}
                                    </Button>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={handleCloseDateRangeDialog}
                        sx={{
                            textTransform: 'none',
                            color: '#3B362A',
                            '&:hover': {
                                backgroundColor: 'rgba(59, 54, 42, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDateRangeApply}
                        variant="contained"
                        disabled={!dateRange.startDate || !dateRange.endDate}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#3B362A',
                            '&:hover': {
                                backgroundColor: '#2a2620'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "#f8f8f4",
                        fontFamily: '"Poppins", sans-serif !important',
                    },
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        bgcolor: "#f4ecd6",
                        color: "#3B362A",
                        borderBottom: "1px solid #efefe7",
                        fontFamily: '"Poppins", sans-serif !important',
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 2,
                            pb: 0,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {activeTab === 0 ? "Subscription Details" : "User Journey"}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDetails}
                            sx={{
                                color: "#666",
                                "&:hover": { color: "#3B362A" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{ px: 2, bgcolor: "#f4ecd6" }}
                    >
                        <Tab label="Details" />
                        <Tab label="History" />
                    </Tabs>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ p: 3 }}>
                    {activeTab === 0 ? (
                        // --- Details Tab ---
                        selectedSubscription ? (
                            <Grid container spacing={4} sx={{ mt: 0 }}>
                                {/* --- Profile Card --- */}
                                {selectedSubscription.profile && (
                                    <Grid item xs={12} sm={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: "#efefe7",
                                                boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, color: "#3B362A", mb: 1 }}
                                            >
                                                User Profile
                                            </Typography>
                                            <Divider sx={{ mb: 1 }} />
                                            <Typography>
                                                <strong>
                                                    {getSubscriptionDisplayName(selectedSubscription)}
                                                </strong>
                                            </Typography>
                                            {selectedSubscription.profile.email && (
                                                <Link
                                                    href={`mailto:${selectedSubscription.profile.email}`}
                                                >
                                                    <Typography>
                                                        Email: {selectedSubscription.profile.email}
                                                    </Typography>
                                                </Link>
                                            )}
                                            {selectedSubscription.profile.phone && (
                                                <Link href={`tel:${selectedSubscription.profile.phone}`}>
                                                    <Typography>
                                                        Phone: {selectedSubscription.profile.phone}
                                                    </Typography>
                                                </Link>
                                            )}
                                            {selectedSubscription.profile.gender && (
                                                <Typography>
                                                    Gender: {selectedSubscription.profile.gender}
                                                </Typography>
                                            )}
                                            {selectedSubscription.profile.dob && (
                                                <Typography>
                                                    DOB: {selectedSubscription.profile.dob}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                )}

                                {/* --- Corporate Card --- */}
                                {selectedSubscription.corporateUser && (
                                    <Grid item xs={12} sm={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: "#efefe7",
                                                boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, color: "#3B362A", mb: 1 }}
                                            >
                                                Corporate Info
                                            </Typography>
                                            <Divider sx={{ mb: 1 }} />
                                            {selectedSubscription?.corporateUser?.company_logo && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <img
                                                        src={selectedSubscription?.corporateUser?.company_logo}
                                                        alt="Company Logo"
                                                        style={{ maxHeight: 50, objectFit: "contain" }}
                                                    />
                                                </Box>
                                            )}

                                            {selectedSubscription.corporateUser.company_name && (
                                                <Typography>
                                                    Company: {selectedSubscription.corporateUser.company_name}
                                                </Typography>
                                            )}
                                            {selectedSubscription.corporateUser.domain && (
                                                <Typography>
                                                    Domain: {selectedSubscription.corporateUser.domain}
                                                </Typography>
                                            )}
                                            {selectedSubscription.corporateUser.email && (
                                                <Typography>
                                                    Email: {selectedSubscription.corporateUser.email}
                                                </Typography>
                                            )}
                                            {selectedSubscription.corporateUser.couponName && (
                                                <Typography>
                                                    Coupon: {selectedSubscription.corporateUser.couponName}
                                                </Typography>
                                            )}
                                            {selectedSubscription.corporateUser.cashRedeem !== undefined && (
                                                <Typography>
                                                    Cash Redeem: {selectedSubscription.corporateUser.cashRedeem}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                )}

                                {/* Subscription Info */}
                                <Grid item xs={12} sm={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: "#efefe7",
                                            boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 600, color: "#3B362A", mb: 1 }}
                                        >
                                            Subscription Info
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <Typography>
                                            Subscription ID: {selectedSubscription.subscriptionId}
                                        </Typography>
                                        <Typography>
                                            UserId: {selectedSubscription?.profile?.UID}
                                        </Typography>
                                        <Typography>
                                            Plan: <strong>{selectedSubscription.planName}</strong>
                                        </Typography>
                                        <Typography>Price: ₹{selectedSubscription.price}</Typography>
                                        {selectedSubscription.discount !== undefined && (
                                            <Typography>Discount: ₹{selectedSubscription.discount}</Typography>
                                        )}
                                        <Typography>
                                            Final Price: <strong>₹{selectedSubscription.finalPrice}</strong>
                                        </Typography>
                                        <Typography>
                                            Teleconsultation Limit:{" "}
                                            {selectedSubscription.teleconsultationlimit}
                                        </Typography>
                                        {selectedSubscription.purchaseDate && (
                                            <Typography>
                                                Purchased:{" "}
                                                {format(
                                                    new Date(selectedSubscription.purchaseDate),
                                                    "MMM dd, yyyy"
                                                )}
                                            </Typography>
                                        )}
                                        {selectedSubscription.expirationDate && (
                                            <Typography>
                                                Expires:{" "}
                                                {format(
                                                    new Date(selectedSubscription.expirationDate),
                                                    "MMM dd, yyyy"
                                                )}
                                            </Typography>
                                        )}
                                        <Box sx={{ mt: 1 }}>
                                            <Chip
                                                label={selectedSubscription.status}
                                                color={
                                                    selectedSubscription.status === "Active"
                                                        ? "success"
                                                        : selectedSubscription.status === "Expired"
                                                            ? "error"
                                                            : "default"
                                                }
                                                size="small"
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Payment Details */}
                                {(selectedSubscription.payment_status ||
                                    selectedSubscription.paymentDetails) && (
                                        <Grid item xs={12} sm={6}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: "#efefe7",
                                                    boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{ fontWeight: 600, color: "#3B362A", mb: 1 }}
                                                >
                                                    Payment Details
                                                </Typography>
                                                <Divider sx={{ mb: 1 }} />
                                                {selectedSubscription.payment_status && (
                                                    <Typography>
                                                        Status: {selectedSubscription.payment_status}
                                                    </Typography>
                                                )}
                                                {selectedSubscription.paymentDetails?.transactionId && (
                                                    <Typography>
                                                        Txn ID: {selectedSubscription.paymentDetails.transactionId}
                                                    </Typography>
                                                )}
                                                {selectedSubscription.paymentDetails?.paymentMethod && (
                                                    <Typography>
                                                        Method: {selectedSubscription.paymentDetails.paymentMethod}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    )}

                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: "#efefe7",
                                            boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, color: "#3B362A" }}
                                            >
                                                Remarks
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleAddRemarkClick}
                                                sx={{ textTransform: "none", borderRadius: 2 }}
                                            >
                                                Add Remark
                                            </Button>
                                        </Box>

                                        <Divider sx={{ mb: 1 }} />

                                        {remarks.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No remarks yet.
                                            </Typography>
                                        ) : (
                                            <List dense>
                                                {remarks.map((r) => (
                                                    <ListItem
                                                        key={r.remark_id}
                                                        disableGutters
                                                        secondaryAction={
                                                            <IconButton
                                                                edge="end"
                                                                size="small"
                                                                onClick={() => handleEditRemarkClick(r)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={r.remark_name}
                                                            secondary={
                                                                r.remark_updated_at
                                                                    ? `Updated: ${format(
                                                                        new Date(r.remark_updated_at),
                                                                        "MMM dd, yyyy h:mm a"
                                                                    )}${r.remark_completed ? " · Completed" : ""
                                                                    }`
                                                                    : r.remark_completed
                                                                        ? "Completed"
                                                                        : undefined
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                </Grid>

                            </Grid>
                        ) : (
                            <Typography color="text.secondary">
                                No subscription selected.
                            </Typography>
                        )
                    ) : (
                        // --- User Journey Tab ---
                        <>
                            {trackingLoading ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="300px"
                                >
                                    <CircularProgress sx={{ color: "#3B362A" }} />
                                </Box>
                            ) : trackingData.length === 0 ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="200px"
                                >
                                    <Typography color="text.secondary">
                                        No activity found for this user.
                                    </Typography>
                                </Box>
                            ) : (
                                <Stepper orientation="vertical" activeStep={-1} sx={{ mt: 2 }}>
                                    {trackingData.map((step, index) => (
                                        <Step key={index} active expanded>
                                            <StepLabel
                                                StepIconComponent={() => (
                                                    <Box
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            borderRadius: "50%",
                                                            bgcolor:
                                                                step.type === "Consultation"
                                                                    ? "#e3f2fd"
                                                                    : "#e8f5e9",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color:
                                                                step.type === "Consultation"
                                                                    ? "primary.main"
                                                                    : "success.main",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                        }}
                                                    >
                                                        {step.type === "Consultation" ? (
                                                            <LocalHospitalIcon fontSize="small" />
                                                        ) : (
                                                            <ShoppingCartIcon fontSize="small" />
                                                        )}
                                                    </Box>
                                                )}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontWeight: 600, color: "#3B362A" }}
                                                    >
                                                        {step.type === "Consultation"
                                                            ? "Consultation"
                                                            : "Order"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(() => {
                                                            const d = step.date ? new Date(step.date) : null;
                                                            return d && !isNaN(d.getTime())
                                                                ? format(d, "MMM dd, yyyy h:mm a")
                                                                : "Date N/A";
                                                        })()}
                                                    </Typography>
                                                </Box>
                                            </StepLabel>
                                            <StepContent>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2,
                                                        bgcolor: "#fff",
                                                        borderRadius: 2,
                                                        border: "1px solid #eee",
                                                    }}
                                                >
                                                    {step.type === "Consultation" ? (
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2">
                                                                    <strong>Status:</strong>{" "}
                                                                    <Chip
                                                                        label={step.status}
                                                                        size="small"
                                                                        color={
                                                                            step.status === "completed"
                                                                                ? "success"
                                                                                : "warning"
                                                                        }
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                                                    />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2">
                                                                    <strong>Doctor:</strong>{" "}
                                                                    {step.details.doctor_name || "N/A"}
                                                                </Typography>
                                                            </Grid>
                                                            {step.status === "rescheduled" && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="body2">
                                                                        <strong>Reason:</strong>{" "}
                                                                        {step.details.consultation_reason || "N/A"}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    ) : (
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2">
                                                                    <strong>Status:</strong>{" "}
                                                                    <Chip
                                                                        label={step.status}
                                                                        size="small"
                                                                        color={
                                                                            step.status === "Delivered"
                                                                                ? "success"
                                                                                : "primary"
                                                                        }
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                                                    />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2">
                                                                    <strong>Items:</strong>
                                                                </Typography>
                                                                <List dense disablePadding>
                                                                    {step.details.Data?.cartitems?.map(
                                                                        (item: any, i: number) => (
                                                                            <ListItem
                                                                                key={i}
                                                                                disablePadding
                                                                                sx={{ pl: 1 }}
                                                                            >
                                                                                <ListItemText
                                                                                    primary={`- ${item.productName || item.name
                                                                                        } (x${item.quantity || item.qty})`}
                                                                                    primaryTypographyProps={{
                                                                                        variant: "body2",
                                                                                        fontSize: "0.8rem",
                                                                                    }}
                                                                                />
                                                                            </ListItem>
                                                                        )
                                                                    )}
                                                                </List>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2">
                                                                    <strong>Total:</strong> ₹
                                                                    {step.details.Data?.total}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Paper>
                                            </StepContent>
                                        </Step>
                                    ))}
                                </Stepper>
                            )}
                        </>
                    )}
                </DialogContent>

                {/* Footer */}
                <DialogActions sx={{ p: 2, bgcolor: "#f4ecd6" }}>
                    <Button
                        onClick={handleCloseDetails}
                        variant="contained"
                        sx={{
                            bgcolor: "#3B362A",
                            "&:hover": { bgcolor: "#2a2620" },
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Close
                    </Button>

                    {activeTab === 0 &&
                        selectedSubscription &&
                        !getSubscriptionInvoiceUrl(selectedSubscription) &&
                        (() => {
                            const isGenerating =
                                generatingInvoiceId === selectedSubscription.subscriptionId;
                            return (
                                <Button
                                    onClick={() =>
                                        handleGenerateSubscriptionInvoice(selectedSubscription)
                                    }
                                    variant="contained"
                                    color="secondary"
                                    startIcon={
                                        isGenerating ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : undefined
                                    }
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? "Generating…" : "Generate"}
                                </Button>
                            );
                        })()}

                    {activeTab === 0 &&
                        selectedSubscription &&
                        getSubscriptionInvoiceUrl(selectedSubscription) && (
                            <Button
                                onClick={() =>
                                    handleViewSubscriptionInvoice(selectedSubscription)
                                }
                                variant="contained"
                                color="secondary"
                            >
                                Download Invoice
                            </Button>
                        )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={remarkDialogOpen}
                onClose={handleCloseRemarkDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "#f8f8f4",
                        fontFamily: '"Poppins", sans-serif !important',
                    },
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        bgcolor: "#f4ecd6",
                        color: "#3B362A",
                        borderBottom: "1px solid #efefe7",
                        py: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {editingRemark ? "Edit Remark" : "Add Remark"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                Keep internal notes about this user / subscription.
                            </Typography>
                        </Box>

                        {editingRemark && (
                            <Chip
                                label="Editing"
                                size="small"
                                sx={{
                                    bgcolor: "#fff",
                                    borderRadius: 999,
                                    fontWeight: 500,
                                }}
                            />
                        )}
                    </Box>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ p: 3 }}>


                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        {/* Remark text */}
                        <Grid item xs={12}>
                            <TextField
                                label="Remark"
                                placeholder="Eg. Called user, explained plan benefits. Will follow up next week."
                                multiline
                                minRows={3}
                                fullWidth
                                value={remarkText}
                                onChange={(e) => setRemarkText(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Updated by */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Updated By"
                                placeholder="Your name / staff ID"
                                fullWidth
                                value={remark_updated_by}
                                onChange={(e) => setremark_updated_by(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Completed checkbox + status hint */}
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: 0.5,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={remarkCompleted}
                                        onChange={(e) => setRemarkCompleted(e.target.checked)}
                                    />
                                }
                                label="Mark as completed"
                            />
                            <Typography variant="caption" sx={{ color: "text.secondary", ml: 4 }}>
                                Use this for tasks or follow-ups that are done.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Footer */}
                <DialogActions
                    sx={{
                        p: 2,
                        bgcolor: "#f4ecd6",
                        borderTop: "1px solid #efefe7",
                    }}
                >
                    <Button
                        onClick={handleCloseRemarkDialog}
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveRemark}
                        variant="contained"
                        disabled={savingRemark}
                        sx={{
                            textTransform: "none",
                            bgcolor: "#3B362A",
                            "&:hover": { bgcolor: "#2a2620" },
                            borderRadius: 2,
                            px: 3,
                        }}
                        startIcon={
                            savingRemark ? <CircularProgress size={16} color="inherit" /> : undefined
                        }
                    >
                        {savingRemark ? "Saving…" : "Save Remark"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 2, fontFamily: '"Poppins", sans-serif !important' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: "#3B362A", pb: 1 }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete {selectedSubscriptions.length} selected subscription{selectedSubscriptions.length > 1 ? 's' : ''}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            bgcolor: "#dc3545",
                            "&:hover": { bgcolor: "#c82333" },
                            borderRadius: 2,
                            textTransform: "none",
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default SubscriptionsPage;